import { resolveLocationTimeZone } from "./location-deadline";
import { parseGmt } from "./print-format";

// AcknowledgeForm rejects a signature over 2 MB decoded
// (InvalidSignatureImage); checked before uploading. A pad PNG is
// typically 10–40 KB, so this only catches a broken export.
export const SIGNATURE_MAX_BYTES = 2 * 1024 * 1024;

export function isSignatureTooLarge(sizeInBytes: number): boolean {
	return sizeInBytes > SIGNATURE_MAX_BYTES;
}

// The pad's PNG as bare base64 (no "data:image/png;base64," prefix), the
// form AcknowledgeForm's signatureImageBase64 takes.
export function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			resolve(String(reader.result).replace(/^data:[^,]*,/, ""));
		};
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(blob);
	});
}

// The text of the error Alert for a failed signed acknowledgement. A 400 is
// about the request itself (usually the signature) and shows the server's
// message. A 413 means the image was too large — often answered by a proxy
// with no JSON body, so it falls back to the client's own "too large" text.
// Null when the server gave nothing specific: the caller shows its generic
// "couldn't save" text.
export function signatureInlineError(
	status: number | null | undefined,
	message: string | null | undefined,
	tooLargeText: string
): string | null {
	if (status === 413) return message || tooLargeText;
	if (status === 400) return message || null;
	return null;
}

// 409: someone else acknowledged the step first. The dialog closes and the
// row is re-read rather than offering a retry that can't succeed.
export function isAlreadyAcknowledgedError(
	status: number | null | undefined
): boolean {
	return status === 409;
}

// Drops a leading/trailing " · " left by an empty part (e.g. a step with no
// acknowledgementDate), so a caption never ends in a dangling separator.
export function trimCaptionSeparators(text: string): string {
	return text.replace(/^\s*·\s*/, "").replace(/\s*·\s*$/, "");
}

// "09/30/2026 2:14 PM EDT" — a GMT acknowledgement time in the location's
// zone (the viewer's when none is given or the name is unknown), with the
// zone abbreviation. Empty when there is no valid time. Takes an IANA name
// or a Windows one ("Eastern Standard Time"), which is what Locations and
// GetProgramDetails' locationTimeZone carry.
export function formatSignedAt(
	value: string | null | undefined,
	timeZone?: string | null
): string {
	if (!value) return "";
	const zone = timeZone ? resolveLocationTimeZone(timeZone) : null;
	const date = parseGmt(value);
	if (Number.isNaN(date.getTime())) return "";
	const options: Intl.DateTimeFormatOptions = {
		month: "2-digit",
		day: "2-digit",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
		timeZoneName: "short",
	};
	let formatter: Intl.DateTimeFormat;
	try {
		formatter = new Intl.DateTimeFormat("en-US", {
			...options,
			timeZone: zone ?? undefined,
		});
	} catch {
		formatter = new Intl.DateTimeFormat("en-US", options);
	}
	const parts: Record<string, string> = Object.fromEntries(
		formatter.formatToParts(date).map((part) => [part.type, part.value])
	);
	return `${parts.month}/${parts.day}/${parts.year} ${parts.hour}:${parts.minute} ${parts.dayPeriod} ${parts.timeZoneName}`;
}

// "10/05/2026" — a worker's decline date (GAT-7804), on the location's
// calendar (the viewer's when no zone is given or the name is unknown). The
// API sends GMT, so near midnight the location's day can differ from the
// viewer's.
export function formatDeclinedDate(
	value: string | null | undefined,
	timeZone?: string | null
): string {
	if (!value) return "";
	const zone = timeZone ? resolveLocationTimeZone(timeZone) : null;
	const date = parseGmt(value);
	if (Number.isNaN(date.getTime())) return "";
	const options: Intl.DateTimeFormatOptions = {
		month: "2-digit",
		day: "2-digit",
		year: "numeric",
	};
	let formatter: Intl.DateTimeFormat;
	try {
		formatter = new Intl.DateTimeFormat("en-US", {
			...options,
			timeZone: zone ?? undefined,
		});
	} catch {
		formatter = new Intl.DateTimeFormat("en-US", options);
	}
	const parts: Record<string, string> = Object.fromEntries(
		formatter.formatToParts(date).map((part) => [part.type, part.value])
	);
	return `${parts.month}/${parts.day}/${parts.year}`;
}

// Today's date as MM/DD/YYYY, shown under the pad next to the signer.
export function formatSigningDate(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${month}/${day}/${date.getFullYear()}`;
}
